import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Truck, Mail, Lock, User, Phone, Building, Loader2, ArrowRight, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: '',
    accountType: 'merchant'
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setError('كلمات المرور غير متطابقة');
      return;
    }
    if (form.password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    setLoading(true);
    try {
      const response = form.accountType === 'delegate'
        ? await api.post('/api/delegate-registration', {
            fullName: form.fullName,
            email: form.email,
            password: form.password,
            phone: form.phone,
            address: form.address
          })
        : await api.post('/api/register', {
            name: form.fullName,
            username: form.email.split('@')[0],
            email: form.email,
            password: form.password,
            phone: form.phone,
            address: form.address,
            companyName: form.companyName
          });

      if (response.data.success) {
        alert('تم التسجيل بنجاح، يرجى انتظار مراجعة الإدارة والموافقة على حسابك.');
        navigate('/login');
      } else {
        setError(response.data.message || 'فشل إنشاء الحساب');
      }
    } catch (err: any) {
      console.error('Registration error details:', err.response?.data);
      setError(err.response?.data?.message || 'حدث خطأ أثناء إنشاء الحساب. تأكد من أن البريد غير مستخدم.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full pl-4 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition";
  const iconClass = "absolute right-3 top-3.5 w-5 h-5 text-gray-400";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50/30 flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">إنشاء حساب جديد</h1>
          <p className="text-gray-600">انضم إلى Target Express وابدأ شحن منتجاتك اليوم</p>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                نوع الحساب
              </label>

              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, accountType: 'merchant' })}
                  className={`min-w-0 p-3 sm:p-4 rounded-xl border-2 transition text-center ${
                    form.accountType === 'merchant'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-orange-300'
                  }`}
                >
                  <div className="text-lg sm:text-2xl font-semibold leading-tight break-words mb-1">Merchant</div>
                  <div className="font-bold">تاجر</div>
                  <div className="text-[11px] sm:text-xs mt-1 leading-5 break-words">حساب شحن وادارة الطلبات</div>
                </button>

                <button
                  type="button"
                  onClick={() => setForm({ ...form, accountType: 'delegate' })}
                  className={`min-w-0 p-3 sm:p-4 rounded-xl border-2 transition text-center ${
                    form.accountType === 'delegate'
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-orange-300'
                  }`}
                >
                  <div className="text-lg sm:text-2xl font-semibold leading-tight break-words mb-1">Delegate</div>
                  <div className="font-bold">مندوب</div>
                  <div className="text-[11px] sm:text-xs mt-1 leading-5 break-words">حساب مندوب توصيل</div>
                </button>
              </div>
            </div>

            <div className="relative">
              <User className={iconClass} />
              <input required name="fullName" type="text" placeholder="الاسم الكامل" value={form.fullName} onChange={handleChange} className={inputClass} />
            </div>

            <div className="relative">
              <Building className={iconClass} />
              <input name="companyName" type="text" placeholder="اسم الشركة (اختياري)" value={form.companyName} onChange={handleChange} className={inputClass} />
            </div>

            <div className="relative">
              <Mail className={iconClass} />
              <input required name="email" type="email" placeholder="البريد الإلكتروني" value={form.email} onChange={handleChange} className={inputClass} />
            </div>

            <div className="relative">
              <Phone className={iconClass} />
              <input required name="phone" type="tel" placeholder="رقم الهاتف (مثال: 01000000000)" value={form.phone} onChange={handleChange} className={inputClass} />
            </div>

            <div className="relative">
              <MapPin className={iconClass} />
              <input name="address" type="text" placeholder="العنوان بالتفصيل (لطلبات الالتقاط)" value={form.address} onChange={handleChange} className={inputClass} />
            </div>

            <div className="relative">
              <Lock className={iconClass} />
              <input required name="password" type="password" placeholder="كلمة المرور" value={form.password} onChange={handleChange} className={inputClass} />
            </div>

            <div className="relative">
              <Lock className={iconClass} />
              <input required name="confirmPassword" type="password" placeholder="تأكيد كلمة المرور" value={form.confirmPassword} onChange={handleChange} className={inputClass} />
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 rounded-xl mt-2">
              {loading ? <Loader2 className="w-5 h-5 animate-spin ml-2" /> : null}
              {loading ? 'جاري إنشاء الحساب...' : 'إنشاء الحساب'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            لديك حساب بالفعل؟{' '}
            <Link to="/login" className="text-orange-600 font-semibold hover:text-orange-700 flex items-center justify-center gap-1 mt-1">
              <ArrowRight className="w-4 h-4" /> تسجيل الدخول
            </Link>
          </div>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-1">
            <ArrowRight className="w-4 h-4" /> العودة للصفحة الرئيسية
          </Link>
        </div>
      </div>
    </div>
  );
}
